import fieldAppImg from "@/assets/catalog/service-line/field-app-job-details.webp";
import callCenterImg from "@/assets/catalog/service-line/call-center-calls-list.webp";
import jobCostingImg from "@/assets/catalog/service-line/job-costing-overview.webp";
import type { CpAccordionItem } from "../../components/CpAccordion/CpAccordion";

export const CTA_HREF =
  "https://scheduler.zoom.us/chris-yingling/certainpath-software-discussion";

export const TIERS = ["Build", "Grow", "Lead", "Scale"];

export interface ServiceLineModule {
  icon: string;
  title: string;
  sub: string;
  href: string;
}

export const MODULES: ServiceLineModule[] = [
  {
    icon: "smartphone",
    title: "Field App",
    sub: "Pricing, invoicing, photos, financing",
    href: "#field-app",
  },
  {
    icon: "headset",
    title: "Call Center & CRM",
    sub: "Intake, scheduling, dispatch, debrief",
    href: "#call-center",
  },
  {
    icon: "chart-line",
    title: "Real-Time Job Costing",
    sub: "Gross margin on every call",
    href: "#job-costing",
  },
  {
    icon: "life-buoy",
    title: "Implementation",
    sub: "Specialists, training and coaching",
    href: "#implementation",
  },
];

export interface ServiceLineFeature {
  id: string;
  eyebrow: string;
  img: string;
  alt: string;
  title: string;
  lead: string;
  points: string[];
}

export const FEATURES: ServiceLineFeature[] = [
  {
    id: "field-app",
    eyebrow: "Field App",
    img: fieldAppImg,
    alt: "CertainPath Software field app job details screen",
    title: "Simplify the Field App So Your Team Focuses on Service",
    lead: "Everything your technicians need to run a successful service call, in the palm of their hand — and every step intuitive.",
    points: [
      "Pricing and options",
      "Invoicing",
      "Materials",
      "Job photos",
      "Financing options",
      "One easy-to-use interface",
    ],
  },
  {
    id: "call-center",
    eyebrow: "Call Center & CRM",
    img: callCenterImg,
    alt: "CertainPath Software calls list",
    title: "Run Your Call Center Like Mission Control",
    lead: "A complete call intake and management system that gets the right tech on the right call — and makes sure nothing gets missed.",
    points: [
      "Call scripts",
      "Customer records (CRM)",
      "Scheduling",
      "Dispatch tools",
      "Debrief interface",
      "Every call tracked",
    ],
  },
  {
    id: "job-costing",
    eyebrow: "Real-Time Numbers",
    img: jobCostingImg,
    alt: "CertainPath Software job overview showing revenue, costs and gross margin",
    title: "Know You're Making Money on Every Call — in Real Time",
    lead: "Stop waiting until month-end to find out if you made money. Adjust in moments, not weeks.",
    points: [
      "Gross margin on every call",
      "Instant job costing",
      "Key metrics at the push of a button",
      "See where you're making money",
    ],
  },
];

export const FEATURE_BGS = ["var(--white)", "var(--cream-100)", "var(--white)"];

export interface IconCopy {
  icon: string;
  title: string;
  body: string;
}

export const KEY_FEATURES: IconCopy[] = [
  {
    icon: "route",
    title: "Dispatch",
    body: "Quickly view technician availability and assign jobs with ease, so the right person is always in the right place at the right time.",
  },
  {
    icon: "users",
    title: "Customers",
    body: "Track every customer and lead in one system, with a complete view of your relationships and sales opportunities.",
  },
  {
    icon: "map-pin",
    title: "GPS Tracking",
    body: "Monitor your fleet in real time with GPS location services to maximize efficiency and cut wasted time on the road.",
  },
  {
    icon: "clipboard-list",
    title: "Jobs",
    body: "Stay on top of scheduled jobs, technician debriefs, material receipts, permits and warranty claims — all in one place.",
  },
  {
    icon: "receipt",
    title: "Invoices",
    body: "Easily create and track invoices, synced with QuickBooks Online and QuickBooks Desktop.",
  },
  {
    icon: "phone-call",
    title: "Calls",
    body: "Track and record every incoming call to improve customer service, measure performance and capture every opportunity.",
  },
  {
    icon: "package",
    title: "Inventory",
    body: "Manage inventory across your warehouse and service trucks so your team is always stocked for the next job.",
  },
  {
    icon: "book-open",
    title: "Pricebook",
    body: "Give customers peace of mind with flat-rate, transparent pricing in an easy-to-update digital price book.",
  },
  {
    icon: "chart-column",
    title: "Reports",
    body: "Actionable reporting for managers, technicians, marketing, memberships, profit margins, payroll and more.",
  },
  {
    icon: "trending-up",
    title: "Forecasting & Budgeting",
    body: "Forecast sales, set budgets and track performance over time.",
  },
];

export const STEPS: IconCopy[] = [
  {
    icon: "user-check",
    title: "Implementation Specialists",
    body: "Dedicated help from day one to get your company set up.",
  },
  {
    icon: "graduation-cap",
    title: "Onboarding Training",
    body: "Training for your team at no extra cost to you.",
  },
  {
    icon: "plug",
    title: "Practically Plug and Play",
    body: "Your current team has what it needs — no outside consultants.",
  },
  {
    icon: "messages-square",
    title: "Coaches Who Know It",
    body: "CertainPath coaches help your team do their jobs in the software.",
  },
];

export const FAQS: CpAccordionItem[] = [
  {
    title: "Who is ServiceLine for?",
    body: "HVAC, Plumbing and Electrical companies of any size that want field, call center and financial tools in one system.",
  },
  {
    title: "Does ServiceLine work for roofing companies?",
    body: "Not yet. At this time CertainPath Software isn't configured for the demands of roofing companies.",
  },
  {
    title: "How long does implementation take?",
    body: "ServiceLine is built to be practically plug and play. Implementation specialists and onboarding training get you up and running fast, without outside consultants.",
  },
  {
    title: "Is onboarding an extra cost?",
    body: "No. Implementation help and onboarding training are included at no extra cost.",
  },
  {
    title: "How do I get started?",
    body: "Book a software discussion and one of our team members will walk you through it.",
  },
];
